import { CiapCidSelectModel, isCiap } from 'components/form/field/select/CiapCidSelectField/CiapCidSelectField'
import { AggregatorEnum } from 'components/pivot-table/components/aggregator/model'
import { PivotTableBoardOrigin } from 'components/pivot-table/components/board/model'
import { PivotTableInitialValues } from 'components/pivot-table/PivotTable'
import {
  ModeloPersonalizadoRelatorioGerencial,
  ModeloPersonalizadoRelatorioGerencialInput,
  ModeloRelatorioGerencial,
  OpcaoSelecionadaRelatorioGerencialEnum,
  TipoAgrupamentoRelatorioGerencialEnum,
  TipoModuloEnum,
  UnidadePeriodoRelatorioGerencialEnum,
} from 'graphql/types.generated'
import { Permission } from 'types/Permissions'
import { emptyArrayOf, partition } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'

import { KeyMapping } from '../../common/keyMapping-relatorioGerencial'
import { calculatePeriodoRelatorioGerencial } from '../form/calculator-relatorioGerencial'
import { RelatorioGerencialFiltrosFormModel } from '../form/model-relatorioGerencialFiltrosForm'
import {
  ModeloPersonalizadoRelatorioGerencialFormModel,
  VisibilidadeModeloPersonalizadoEnum,
} from './form/model-modeloPersonalizadoRelatorioGerencialForm'
import { isCampoInconsistente } from './utils-modeloPersonalizado'

// TODO (@ElDorado) Quando finalização migração da PivotTable pro Bold, atualizar modelos salvos para antes de alterar PosicaoCampoRelatorioGerencialEnum para PivotTableBoardOrigin
const posicaoToOrigin: Record<string, PivotTableBoardOrigin> = {
  FILTRO: 'filter',
  LINHA: 'row',
  COLUNA: 'column',
}

export function modeloToPivotTableProps<T>(
  modelo: ModeloRelatorioGerencial,
  keyMapping: Map<string, KeyMapping>
): PivotTableInitialValues<T> {
  return modelo
    ? {
        fields: modelo.campos
          .filter((campo) => !isUndefinedOrNull(keyMapping.get(campo.key)?.modelo))
          .map((campo) => ({
            filters: [...campo.items],
            key: campo.key as keyof T,
            origin: posicaoToOrigin[campo.posicao],
          })),
        aggregatorId: AggregatorEnum[modelo.aggregator],
      }
    : null
}

export function modeloPersonalizadoToFiltrosFormModel(
  modeloPersonalizado: ModeloPersonalizadoRelatorioGerencial,
  keyMapping: Map<string, KeyMapping>,
  modulo: TipoModuloEnum,
  isGestorEstadual: boolean,
  hasFiltroPeriodo: boolean,
  hasFiltroTipoAgrupamento: boolean,
  hasAuthorization: (permission: Permission) => boolean
): RelatorioGerencialFiltrosFormModel {
  return {
    ciapsCids: [...(modeloPersonalizado.modelo.ciaps ?? []), ...(modeloPersonalizado.modelo.cids ?? [])],
    gruposCondicoesPrioritarios: modeloPersonalizado.modelo.gruposCondicoes,
    opcaoSelecionada: hasFiltroPeriodo
      ? modeloPersonalizado.modelo.opcaoSelecionada ?? OpcaoSelecionadaRelatorioGerencialEnum.PRIMEIRA
      : undefined,
    unidade: hasFiltroPeriodo
      ? modeloPersonalizado.modelo.unidadePeriodo ?? UnidadePeriodoRelatorioGerencialEnum.MES
      : undefined,
    modeloPersonalizado: {
      id: modeloPersonalizado.id,
      nome: modeloPersonalizado.nome,
      hasCampoInconsistente: modeloPersonalizado.modelo.campos.some(({ key }) =>
        isCampoInconsistente(key, keyMapping, modulo, isGestorEstadual, hasAuthorization)
      ),
      isPublico: modeloPersonalizado.isPublico,
    },
    periodo: calculatePeriodoRelatorioGerencial(
      modeloPersonalizado?.modelo.unidadePeriodo,
      modeloPersonalizado?.modelo.opcaoSelecionada,
      null
    ),
    tipoAgrupamento: hasFiltroTipoAgrupamento
      ? modeloPersonalizado.modelo.tipoAgrupamento ?? TipoAgrupamentoRelatorioGerencialEnum.PESSOAS
      : undefined,
  }
}

export const relatorioGerencialFormModelToJsonModel = (
  values: ModeloPersonalizadoRelatorioGerencialFormModel
): ModeloPersonalizadoRelatorioGerencialInput => {
  const [ciaps, cids] = partition(values.ciapsCids, (ciapCid) => isCiap(ciapCid))
  const typedCids = cids || emptyArrayOf<CiapCidSelectModel>()
  return {
    aggregator: values.aggregatorModal,
    ciapIds: ciaps?.map((ciapCid) => ciapCid.id),
    cidIds: typedCids.map((ciapCid) => ciapCid.id),
    campos: values.campos.map((campo) => ({
      key: campo.key,
      posicao: campo.posicao,
      items: campo.items,
    })),
    gruposCondicoesIds: values.gruposCondicoes?.map((grupoCondicao) => grupoCondicao.id),
    opcaoSelecionada: values.opcaoSelecionadaModal,
    unidadePeriodo: values.unidadePeriodoModal,
    tipoAgrupamento: values.tipoAgrupamentoModal,
  }
}

export function visibilidadeModeloPersonalizadoEnumToIsPublico(visibilidade: VisibilidadeModeloPersonalizadoEnum) {
  return visibilidade === VisibilidadeModeloPersonalizadoEnum.PUBLICO
}

export function isPublicoToVisibilidadeModeloPersonalizadoEnum(
  isPublico: boolean
): VisibilidadeModeloPersonalizadoEnum {
  return isPublico ? VisibilidadeModeloPersonalizadoEnum.PUBLICO : VisibilidadeModeloPersonalizadoEnum.PRIVADO
}
